import { ref, useContext } from '@nuxtjs/composition-api';
import { Logger, useVSFContext } from '@vue-storefront/core';

import loadCart from './loadCart';
import { BuyerIdentity, Cart, LineItem } from '@api-client/src/types';
import useCartStorage from './useCartStorage';

interface UseCartErrors {
  loadCart: string | null;
  addItem: string | null;
  addItems: string | null;
  updateItemQty: string | null;
  removeItem: string | null;
  cartBuyerIdentityUpdate: string | null;
}

interface IProductToAdd {
  variantId: string;
  quantity: number;
}

interface ICustomQuery {
  key: string;
  value: string;
}

const cart = ref<Cart | null>(null);
const loading = ref(false);

const useCart = () => {
  const context = useVSFContext();
  const { $config } = useContext();
  const { getCartIdFromStorage } = useCartStorage(context);

  const error = ref<UseCartErrors>({
    loadCart: null,
    addItem: null,
    addItems: null,
    updateItemQty: null,
    removeItem: null,
    cartBuyerIdentityUpdate: null,
  });

  const setError = (key: keyof UseCartErrors, message: string | null) => {
    error.value = { ...error.value, [key]: message };

    if (message) {
      Logger.error(key, message);
    }
  };

  const handleLoadCart = async (
    buyer: BuyerIdentity | null = null,
    customVariables?: Record<string, unknown>,
  ) => {
    try {
      loading.value = true;
      setError('loadCart', null);

      cart.value = await loadCart({
        buyer,
        context,
        customVariables,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load cart';

      setError('loadCart', errorMessage);
    } finally {
      loading.value = false;
    }
  };

  const getCartId = async () => {
    const cartStorage = getCartIdFromStorage();

    if (!cartStorage) {
      await handleLoadCart();
    }

    return cart.value?.id;
  };

  const handleCartBuyerIdentityUpdate = async (buyer: BuyerIdentity) => {
    try {
      loading.value = true;
      setError('cartBuyerIdentityUpdate', null);

      const cartId = await getCartId();

      if (cartId) {
        cart.value = await context.$shopify.api.cartBuyerIdentityUpdate({
          buyerIdentity: buyer,
          cartId,
        });
      } else {
        throw new Error('Failed to get cart ID.');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to update cart buyer identity.';

      setError('cartBuyerIdentityUpdate', errorMessage);
    } finally {
      loading.value = false;
    }
  };

  /**
   * It is a LineItem but keep it as product to ensure brands dont break.
   */
  const handleUpdateItemQty = async ({
    product: lineItem,
    quantity,
  }: {
    product: LineItem;
    quantity: number;
  }) => {
    try {
      loading.value = true;
      setError('updateItemQty', null);

      const cartId = await getCartId();

      if (cartId) {
        cart.value = await context.$shopify.api.cartLinesUpdate({
          cartId,
          lines: [{ id: lineItem.id, merchandiseId: lineItem.merchandiseId, quantity }],
        });
      } else {
        throw new Error('Failed to get cart ID.');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Sorry, we could not update the quantity of this item in your cart.';

      setError('updateItemQty', errorMessage);
    } finally {
      loading.value = false;
    }
  };

  const handleAddMultipleItems = async (products: IProductToAdd[]) => {
    try {
      loading.value = true;
      setError('addItems', null);

      const mappedLineItems = products.map((p) => ({
        merchandiseId: p.variantId,
        quantity: p.quantity,
        attributes: undefined,
      }));

      const cartId = await getCartId();

      if (cartId) {
        cart.value = await context.$shopify.api.cartLinesAdd({
          cartId,
          lines: mappedLineItems,
          cartEndpoint: $config.cartEndpoint,
          shopifyStorefrontToken: $config.shopifyStorefrontToken,
        });
      } else {
        throw new Error('Failed to get cart ID.');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Sorry, we could not add these items to your cart.';

      setError('addItems', errorMessage);
    } finally {
      loading.value = false;
    }
  };

  const handleAddItem = async ({
    product: lineItem,
    quantity,
    customQuery,
  }: {
    product: { variantId: string };
    quantity: number;
    customQuery?: ICustomQuery[];
  }) => {
    try {
      loading.value = true;
      setError('addItem', null);

      const merchandiseId = lineItem.variantId;

      const cartId = await getCartId();

      if (cartId) {
        cart.value = await context.$shopify.api.cartLinesAdd({
          cartId,
          lines: [{ merchandiseId, quantity, attributes: customQuery }],
          cartEndpoint: $config.cartEndpoint,
          shopifyStorefrontToken: $config.shopifyStorefrontToken,
        });
      } else {
        throw new Error('Failed to get cart ID.');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Sorry, we could not add this item to your cart.';

      setError('addItem', errorMessage);
    } finally {
      loading.value = false;
    }
  };

  const handleRemoveItem = async ({ product: lineItem }: { product: LineItem }) => {
    try {
      loading.value = true;
      setError('removeItem', null);

      const cartId = await getCartId();

      if (cartId) {
        cart.value = await context.$shopify.api.cartLinesRemove({
          cartId,
          lineIds: [lineItem.id],
          cartEndpoint: $config.cartEndpoint,
          shopifyStorefrontToken: $config.shopifyStorefrontToken,
        });
      } else {
        throw new Error('Failed to get cart ID.');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Sorry, we could not remove this item from your cart.';

      setError('removeItem', errorMessage);
    } finally {
      loading.value = false;
    }
  };

  return {
    cart,
    error,
    load: handleLoadCart,
    updateItemQty: handleUpdateItemQty,
    addItem: handleAddItem,
    addItems: handleAddMultipleItems,
    cartBuyerIdentityUpdate: handleCartBuyerIdentityUpdate,
    removeItem: handleRemoveItem,
    loading,
  };
};

export default useCart;
